// @flow
import React, { useEffect, useState, useCallback, useMemo } from 'react'
import * as DmsCommon from './../supports/dms-common';
import * as DmsService from './../supports/dms-service';
import { GlobalEvent, GlobalState } from './GlobalContext'
import { isOfficeFile, openOfficeFileInNewTab } from './FileViewer';
import MsgDialog from './MsgDialog';
import DocRevAddDialog from './DocRevAddDialog';
import PropTypes from 'prop-types';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddIcon from '@mui/icons-material/Add';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import dayjs from "dayjs"


import { AgGridColumn, AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-community/styles/ag-theme-material.css';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DocRevCompareDialog from './DocRevCompareDialog';

export default function DocRevTab(props) {
  const { sysID, docDetail, afterUpdate, isFolderOffline } = props;

  // Memoize gridData so reference is stable between renders
  const gridData = useMemo(() => {
    const revisions = docDetail?.Revisions || [];
    return revisions.map(item => ({
      ...item,
      FileSizeDisplay: DmsCommon.getReadableFileSizeString(item.FileSize),
      UploadDateJs: dayjs(item.UploadDate),
      UploadDateDisplay: dayjs(item.UploadDate).format(DmsCommon.SHORT_DATE_FORMAT),
    }));
  }, [docDetail?.Revisions]);

  // Get Global state and dispatcher
  const [gState, gStateDispatch] = React.useContext(GlobalState);
  const [gEvent, gEventDispatch] = React.useContext(GlobalEvent);

  const [showLoading, setShowLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const onCatchPromiseError = (e) => {
    gEventDispatch({
      showErr: true,
      err: e,
    });
  }

  const comparatorFileSize = (a, b, nodeA, nodeB) => {
    return nodeA.data.FileSize - nodeB.data.FileSize;
  }

  const comparatorUploadDate = (a, b, nodeA, nodeB) => {
    const [aDate, bDate] = [nodeA.data.UploadDateJs, nodeB.data.UploadDateJs];
    if (aDate.isBefore(bDate)) {
      return -1;
    } else if (aDate.isAfter(bDate)) {
      return 1;
    } else {
      return 0;
    }
  }

  const [selectedRecs, setSelectedRecs] = useState([]);

  const onSelectionChanged = useCallback((params) => {
    const nodes = params.api.getSelectedNodes();
    const selRecs = nodes.map(item => item.data);
    setSelectedRecs(selRecs);
  }, []);

  const handleGetRowId = useCallback((params) => String(params.data.RevNo), []);

  const handleOpenClick = () => {
    if (isFolderOffline) {
      setErrMsg("The path is offline");
      setShowError(true);
      return;
    }

    if (docDetail.Permission < DmsCommon.AccessRightEnum.arReadDoc) {
      setErrMsg("Can't open document. Access denied!");
      setShowError(true);
      return
    }

    if (docDetail.Filename === "") {
      setErrMsg("Blank File can't be viewed or downloaded!");
      setShowError(true);
      return
    }

    if (selectedRecs.length === 1) {
      const selRec = selectedRecs[0];
      const vFileExt = DmsCommon.getFileExtension(selRec.FileName);

      if (isOfficeFile(selRec.FileName)) {
        const vDownloadURL = DmsService.getDownloadUrlWithFilename(gState.sessionID, sysID, selRec.DocID, selRec.RevNo, null, null, null, 0, selRec.FileName);
        openOfficeFileInNewTab({
          url: vDownloadURL,
          filename: selRec.FileName,
          title: `${docDetail?.Subject || selRec.FileName} (Rev. ${selRec.RevNo})`,
          canDownload: docDetail?.Permission >= DmsCommon.AccessRightEnum.arReadDn,
        });
      } else {
        const vIsAttach = (vFileExt == '.pdf' || vFileExt == '.txt') ? 0 : 1;
        const vDownloadURL = DmsService.getDownloadUrl(gState.sessionID, sysID, selRec.DocID, selRec.RevNo, null, null, null, vIsAttach);
        window.open(vDownloadURL, '_blank');
      }

      DmsService.accessLogPromise(gState.sessionID, sysID, DmsCommon.DmsLogEnum.daOpenRevision, `${selRec.DocID}_${selRec.RevNo}`);
      DmsService.updateDocDListLogPromise(gState.sessionID, sysID, selRec.DocID, selRec.RevNo);

      afterUpdate && afterUpdate();
    }
  }

  const handleDownloadClick = () => {
    if (isFolderOffline) {
      setErrMsg("The path is offline");
      setShowError(true);
      return;
    }

    if (docDetail.Permission < DmsCommon.AccessRightEnum.arReadDn) {
      setErrMsg("Can't download document. Access denied!");
      setShowError(true);
      return
    }

    if (docDetail.Filename === "") {
      setErrMsg("Blank File can't be viewed or downloaded!");
      setShowError(true);
      return
    }

    if (selectedRecs.length === 1) {
      const selRec = selectedRecs[0];

      DmsService.downloadFileStream(
        gState.sessionID,
        sysID,
        selRec.DocID,
        selRec.RevNo,
        null,
        null,
        null,
        selRec.FileName,
        () => setShowLoading(true),
        () => setShowLoading(false),
        (err) => {
          setErrMsg(err);
          setShowError(true);
        }
      );

      DmsService.accessLogPromise(gState.sessionID, sysID, DmsCommon.DmsLogEnum.daOpenRevision, `${selRec.DocID}_${selRec.RevNo}`);
      DmsService.updateDocDListLogPromise(gState.sessionID, sysID, selRec.DocID, selRec.RevNo);

      afterUpdate && afterUpdate();
    }
  }

  const [openAddRevisionDialog, setOpenAddRevisionDialog] = useState(false);
  const [openCompareDialog, setOpenCompareDialog] = useState(false);

  const handleAfterUpdate = () => {
    setOpenAddRevisionDialog(false);
    afterUpdate && afterUpdate();
  }

  const cellStyle = { paddingLeft: '10px', paddingRight: '10px', fontSize: '0.8rem' };

  const compareStrInsensitive = (aStrA, aStrB) => {
    return aStrA.localeCompare(aStrB, undefined, { sensitivity: "base" })
  }

  const comparatorRemark = (a, b, nodeA, nodeB) => {
    return compareStrInsensitive(nodeA.data.Remark, nodeB.data.Remark)
  }

  const comparatorFileName = (a, b, nodeA, nodeB) => {
    return compareStrInsensitive(nodeA.data.FileName, nodeB.data.FileName)
  }

  const comparatorUploadUserName = (a, b, nodeA, nodeB) => {
    return compareStrInsensitive(nodeA.data.UploadUserName, nodeB.data.UploadUserName)
  }

  const columnDefs = useMemo(() => [
    {
      field: 'RevNo',
      headerName: "Rev.",
      cellStyle: cellStyle,
      width: 110,
      sort: "asc",
      checkboxSelection: true,
      headerCheckboxSelection: false,
    },
    {
      field: 'Remark', headerName: "Remarks", cellStyle: cellStyle, width: 120, autoHeight: true,
      wrapText: true, comparator: comparatorRemark
    },
    {
      field: 'FileName', headerName: "FileName", cellStyle: cellStyle, minWidth: 150, flex: "1",
      autoHeight: true, wrapText: true, comparator: comparatorFileName
    },
    {
      field: 'FileSizeDisplay', headerName: "File Size", cellStyle: cellStyle, width: 120,
      comparator: comparatorFileSize
    },
    {
      field: 'UploadUserName', headerName: "Uploaded By", cellStyle: cellStyle, width: 150,
      comparator: comparatorUploadUserName
    },
    {
      field: 'UploadDateDisplay', headerName: "Upload Date", cellStyle: cellStyle,
      comparator: comparatorUploadDate
    },
  ], []);



  return (
    // <Box component="div" sx={{ display: 'flex', p: 0, height: 1, maxHeight: 1, overflow: 'auto', bgcolor: 'grey.300' }} >
    <Box sx={{ display: 'flex', flexDirection: 'column', height: "70vh" }} >
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <MsgDialog
        open={showError}
        title="Error"
        msgText={errMsg}
        onClose={() => setShowError(false)}
      />

      <Box sx={{ py: 1, px: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }} >

        <Button startIcon={<AddIcon />} onClick={() => {
          if (isFolderOffline) {
            setErrMsg("The path is offline");
            setShowError(true);
            return;
          }
          setOpenAddRevisionDialog(true);
        }}>
          Add
        </Button>
        <DocRevAddDialog
          sysID={sysID}
          open={openAddRevisionDialog}
          onClose={() => setOpenAddRevisionDialog(false)}
          docDetail={docDetail}
          afterUpdate={handleAfterUpdate}
        />

        <Button disabled={selectedRecs.length !== 1} startIcon={<OpenInNewIcon />} onClick={handleOpenClick}>
          Open
        </Button>

        <Button disabled={selectedRecs.length !== 1} startIcon={<CloudDownloadIcon />} onClick={handleDownloadClick}>
          Download
        </Button>

        {/* Hide "Compare Revision" button in UI */}
        {false && (
          <Button
            disabled={selectedRecs.length !== 2}
            startIcon={<AutoAwesomeIcon />}
            variant={selectedRecs.length === 2 ? "contained" : "outlined"}
            size="small"
            onClick={() => {
              if (isFolderOffline) {
                setErrMsg("The path is offline");
                setShowError(true);
                return;
              }
              if (selectedRecs.length !== 2) {
                setErrMsg("Please select exactly 2 revisions to compare.");
                setShowError(true);
                return;
              }
              setOpenCompareDialog(true);
            }}
            sx={{
              ml: 1,
              textTransform: 'none',
              fontWeight: 600,
              ...(selectedRecs.length === 2 ? {
                background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
                color: '#fff',
                boxShadow: '0 2px 8px rgba(59, 130, 246, 0.35)',
              } : {})
            }}
          >
            Compare {selectedRecs.length === 2 ? `(Rev ${selectedRecs[0].RevNo} & ${selectedRecs[1].RevNo})` : ''}
          </Button>
        )}

        <DocRevCompareDialog
          open={openCompareDialog}
          onClose={() => setOpenCompareDialog(false)}
          sysID={sysID}
          docDetail={docDetail}
          selectedRevisions={selectedRecs}
        />

      </Box>

      <div className="ag-theme-material" style={{ width: '100%', height: '100%', bgcolor: 'yellow' }}>
        <AgGridReact

          rowData={gridData}

          defaultColDef={{
            width: 100,
            resizable: true,
            sortable: true,
            suppressMovable: true,
          }}
          columnDefs={columnDefs}
          headerHeight={40}

          suppressNoRowsOverlay

          rowSelection="multiple"
          rowMultiSelectWithClick={true}
          onSelectionChanged={onSelectionChanged}
          getRowId={handleGetRowId}
        >


        </AgGridReact>

      </div>

    </Box>
    // <Box sx={{ flexGrow: 1 }}>
    //     <div style={{ width: '100%', height: '100%' }}>

    //     </div>
    // </Box>


  );
}

DocRevTab.propTypes = {
  sysID: PropTypes.string,
  docDetail: PropTypes.object,
  afterUpdate: PropTypes.func,
  isFolderOffline: PropTypes.bool,
};
